/**
 * Venue model for managing rooms and facilities under a therapy center
 */
import { v4 as uuidv4 } from 'uuid';

// Venue types
export const VENUE_TYPES = {
  TREATMENT_ROOM: 'Treatment Room',
  WAITING_AREA: 'Waiting Area',
  CONSULTATION: 'Consultation Room',
  REST_AREA: 'Rest Area',
  OTHER: 'Other'
};

// Venue status
export const VENUE_STATUS = {
  AVAILABLE: 'Available',
  OCCUPIED: 'Occupied',
  MAINTENANCE: 'Maintenance',
  RESERVED: 'Reserved'
};

class Venue {
  /**
   * Constructor
   * @param {Object} data - Venue data
   */
  constructor(data = {}) {
    this.id = data.id || uuidv4();
    this.therapyCenterId = data.therapyCenterId || '';
    this.name = data.name || '';
    this.description = data.description || '';
    this.type = data.type || VENUE_TYPES.TREATMENT_ROOM;
    this.capacity = data.capacity || 1; // Number of people accommodated
    this.location = data.location || '';
    this.floor = data.floor || '';
    this.roomNumber = data.roomNumber || '';
    this.features = data.features || [];
    this.isActive = data.isActive !== undefined ? data.isActive : true;
    this.status = data.status || VENUE_STATUS.AVAILABLE;
    this.maintenanceSchedule = data.maintenanceSchedule || [];
    this.image = data.image || null;
    this.createdAt = data.createdAt || new Date().toISOString();
    this.updatedAt = data.updatedAt || new Date().toISOString();
  }

  /**
   * Update venue data
   * @param {Object} data - Data to update
   * @returns {Venue} Updated instance
   */
  update(data) {
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && key !== 'id') {
        this[key] = data[key];
      }
    });
    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Add a feature
   * @param {string} feature - Feature to add
   * @returns {boolean} Whether added successfully
   */
  addFeature(feature) {
    if (!this.features.includes(feature)) {
      this.features.push(feature);
      this.updatedAt = new Date().toISOString();
      return true;
    }
    return false;
  }

  /**
   * Remove a feature
   * @param {string} feature - Feature to remove
   * @returns {boolean} Whether removed successfully
   */
  removeFeature(feature) {
    const index = this.features.indexOf(feature);
    if (index !== -1) {
      this.features.splice(index, 1);
      this.updatedAt = new Date().toISOString();
      return true;
    }
    return false;
  }

  /**
   * Add a maintenance schedule
   * @param {Date|string} startTime
   * @param {Date|string} endTime
   * @param {string} reason
   * @returns {string} Maintenance schedule ID
   */
  addMaintenanceSchedule(startTime, endTime, reason = '') {
    const id = uuidv4();
    const maintenance = {
      id,
      startTime: startTime instanceof Date ? startTime.toISOString() : startTime,
      endTime: endTime instanceof Date ? endTime.toISOString() : endTime,
      reason,
      createdAt: new Date().toISOString()
    };
    this.maintenanceSchedule.push(maintenance);
    this.updatedAt = new Date().toISOString();
    return id;
  }

  /**
   * Remove a maintenance schedule
   * @param {string} maintenanceId
   * @returns {boolean} Whether removed successfully
   */
  removeMaintenanceSchedule(maintenanceId) {
    const index = this.maintenanceSchedule.findIndex(m => m.id === maintenanceId);
    if (index !== -1) {
      this.maintenanceSchedule.splice(index, 1);
      this.updatedAt = new Date().toISOString();
      return true;
    }
    return false;
  }

  /**
   * Set venue status
   * @param {string} status
   * @returns {Venue} Updated instance
   */
  setStatus(status) {
    if (!Object.values(VENUE_STATUS).includes(status)) {
      throw new Error(`Invalid venue status: ${status}`);
    }
    this.status = status;
    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Check if available in a time range
   * @param {Date|string} startTime
   * @param {Date|string} endTime
   * @returns {boolean} Whether available
   */
  isAvailableAt(startTime, endTime) {
    if (!this.isActive) {
      return false;
    }
    const start = startTime instanceof Date ? startTime : new Date(startTime);
    const end = endTime instanceof Date ? endTime : new Date(endTime);
    for (const maintenance of this.maintenanceSchedule) {
      const maintenanceStart = new Date(maintenance.startTime);
      const maintenanceEnd = new Date(maintenance.endTime);
      if (start < maintenanceEnd && end > maintenanceStart) {
        return false;
      }
    }
    return true;
  }

  /**
   * Activate venue
   * @returns {Venue} Updated instance
   */
  activate() {
    this.isActive = true;
    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Deactivate venue
   * @returns {Venue} Updated instance
   */
  deactivate() {
    this.isActive = false;
    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Convert to JSON object
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      id: this.id,
      therapyCenterId: this.therapyCenterId,
      name: this.name,
      description: this.description,
      type: this.type,
      capacity: this.capacity,
      location: this.location,
      floor: this.floor,
      roomNumber: this.roomNumber,
      features: this.features,
      isActive: this.isActive,
      status: this.status,
      maintenanceSchedule: this.maintenanceSchedule,
      image: this.image,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }

  /**
   * Create venue instance from JSON data
   * @param {Object} json
   * @returns {Venue}
   * @static
   */
  static fromJSON(json) {
    return new Venue(json);
  }
}

export default Venue;